import {
    Building2, Users, Rocket, Heart,
    Cpu, UserCog, Wrench, MessageSquare, Lightbulb,
    GraduationCap, Stethoscope, ShoppingBag, Landmark, Factory,
    Zap, Shield, BarChart2, Globe, Bell, Clock, CheckCircle,
    FileText, Settings, Star, TrendingUp, Lock, Smartphone,
} from 'lucide-react';

// ─── By Company Size ──────────────────────────────────────────────────────────

export const enterpriseData = {
    tag: 'By Company Size',
    tagVariant: 'blue',
    heroIcon: <Building2 size={40} />,
    title: 'Enterprise-Grade Support at Scale',
    subtitle: 'Deploy KaizIQ Resolve across your entire organisation with advanced governance, multi-project isolation, and SLA enforcement that keeps every team accountable.',
    stats: [
        { value: '50+', label: 'Agent seats' },
        { value: '∞', label: 'Ticket volume' },
        { value: '99.9%', label: 'Uptime SLA' },
    ],
    features: [
        { icon: <Shield size={20} />, title: 'Role-Based Access', desc: 'Fine-grained permissions across teams, departments, and projects. SSO and SAML support for seamless enterprise auth.' },
        { icon: <BarChart2 size={20} />, title: 'Custom Dashboards', desc: 'Executive-level visibility with KPI dashboards, trend reports, and SLA compliance metrics across all projects.' },
        { icon: <Globe size={20} />, title: 'Multi-Workspace', desc: 'Manage multiple brands, regions, or departments from a single platform with complete data isolation.' },
        { icon: <Lock size={20} />, title: 'Security & Compliance', desc: 'Audit logs, data encryption at rest and in transit, and SOC 2 Type II compliance built in.' },
        { icon: <Zap size={20} />, title: 'Advanced Automation', desc: 'Unlimited automation rules, escalation matrices, and AI-powered routing to handle enterprise-scale ticket volumes.' },
        { icon: <Smartphone size={20} />, title: 'White-Label Mobile App', desc: 'Deploy a fully branded iOS & Android app to your customers and staff with no development effort.' },
    ],
    steps: [
        { step: '01', title: 'Configure Your Workspace', desc: 'Set up projects, departments, and team structures with our guided onboarding for enterprise accounts.' },
        { step: '02', title: 'Integrate & Automate', desc: 'Connect your existing tools, configure SSO, and set up automation rules tailored to your workflows.' },
        { step: '03', title: 'Deploy & Govern', desc: 'Roll out to your teams with centralised oversight, SLA monitoring, and real-time performance dashboards.' },
    ],
    accentColor: '#1a6fb5',
    ctaTitle: 'Built for organisations that demand more',
    ctaSubtitle: 'Talk to our enterprise team about custom pricing, compliance requirements, and a tailored onboarding plan.',
    ctaButton: 'Contact Sales',
    ctaPath: null,
};

export const smbData = {
    tag: 'By Company Size',
    tagVariant: 'blue',
    heroIcon: <Users size={40} />,
    title: 'Powerful Support for Growing Teams',
    subtitle: "You don't need enterprise complexity to deliver enterprise-quality support. KaizIQ Resolve gives small and medium teams the tools they need to punch above their weight.",
    stats: [
        { value: '3–50', label: 'Agent seats' },
        { value: '14-day', label: 'Free trial' },
        { value: '2 hrs', label: 'Average setup time' },
    ],
    features: [
        { icon: <Zap size={20} />, title: 'Quick Setup', desc: 'Launch your first support form and start receiving tickets in under two hours — no engineering help needed.' },
        { icon: <Bell size={20} />, title: 'WhatsApp Alerts', desc: 'Agents and customers stay in the loop with instant WhatsApp notifications for every ticket update.' },
        { icon: <Clock size={20} />, title: 'SLA Management', desc: 'Set response and resolution targets per priority. Automated escalation keeps tickets from falling through the cracks.' },
        { icon: <BarChart2 size={20} />, title: 'Team Analytics', desc: 'Monitor agent workload, resolution times, and ticket trends to keep your team running efficiently.' },
        { icon: <FileText size={20} />, title: 'Unlimited Forms', desc: 'Create dedicated intake forms for each team, product, or support channel — all from one dashboard.' },
        { icon: <TrendingUp size={20} />, title: 'Grow Without Limits', desc: 'Add agents and features as you scale. Upgrade your plan in one click without migrating data.' },
    ],
    steps: [
        { step: '01', title: 'Sign Up & Onboard', desc: 'Create your workspace, add your team, and customise your first support form in minutes.' },
        { step: '02', title: 'Share Your Form', desc: 'Distribute your public form URL via website, WhatsApp, or QR code. Tickets flow in automatically.' },
        { step: '03', title: 'Resolve & Improve', desc: 'Assign, track, and close tickets efficiently. Use analytics to continuously improve your team performance.' },
    ],
    accentColor: '#1a6fb5',
    ctaTitle: 'Support that grows with you',
    ctaSubtitle: 'Start your 14-day free trial with no credit card required. Switch plans anytime.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const startupsData = {
    tag: 'By Company Size',
    tagVariant: 'blue',
    heroIcon: <Rocket size={40} />,
    title: 'Move Fast Without Breaking Trust',
    subtitle: "Startups can't afford to drop the ball on customer support. KaizIQ Resolve lets you deliver professional, scalable support from day one — without the enterprise price tag.",
    stats: [
        { value: '₹0', label: 'To get started' },
        { value: '<1 hr', label: 'To go live' },
        { value: '100%', label: 'No-code setup' },
    ],
    features: [
        { icon: <Rocket size={20} />, title: 'Launch Instantly', desc: 'Free tier with no credit card. Get your first support form live in under 60 minutes.' },
        { icon: <Globe size={20} />, title: 'Public Support Forms', desc: 'Share a branded form URL with your users. Works on mobile, desktop, and any browser.' },
        { icon: <Bell size={20} />, title: 'WhatsApp-Native', desc: 'Meet your early customers where they already are. Ticket updates delivered straight to WhatsApp.' },
        { icon: <Settings size={20} />, title: 'No Engineering Required', desc: 'Non-technical founders can build, configure, and manage support operations independently.' },
        { icon: <TrendingUp size={20} />, title: 'Investor-Ready Metrics', desc: 'Track resolution times, ticket volumes, and satisfaction trends. Great for support KPIs in your reports.' },
        { icon: <Shield size={20} />, title: 'Scales With Your Raise', desc: 'Upgrade seamlessly as your team grows. Your data stays intact across every plan.' },
    ],
    steps: [
        { step: '01', title: 'Create Your Free Account', desc: 'Sign up in seconds. No credit card, no contracts, no engineering help needed.' },
        { step: '02', title: 'Build Your Support Form', desc: 'Use the no-code form builder to capture exactly the information your team needs.' },
        { step: '03', title: 'Delight Your Users', desc: 'Resolve tickets fast, send WhatsApp updates, and turn support into a competitive advantage.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Your users deserve great support from day one',
    ctaSubtitle: 'Start free. Upgrade when you are ready. No lock-in.',
    ctaButton: 'Get Started Free',
    ctaPath: '/login',
};

export const nonprofitsData = {
    tag: 'By Company Size',
    tagVariant: 'blue',
    heroIcon: <Heart size={40} />,
    title: 'Do More Good With Streamlined Operations',
    subtitle: 'Nonprofits and NGOs have the biggest missions and the tightest budgets. KaizIQ Resolve helps your team operate efficiently so you can focus on impact — not inbox chaos.',
    stats: [
        { value: 'Special', label: 'NGO pricing' },
        { value: '∞', label: 'Volunteers supported' },
        { value: '0', label: 'IT team needed' },
    ],
    features: [
        { icon: <Heart size={20} />, title: 'NGO-Friendly Pricing', desc: 'Special rates for registered non-profits and social enterprises. Reach out to discuss eligibility.' },
        { icon: <Users size={20} />, title: 'Volunteer Coordination', desc: 'Manage requests from volunteers, donors, and beneficiaries from a single, unified platform.' },
        { icon: <Globe size={20} />, title: 'Multi-Program Support', desc: 'Run separate intake forms for each program, region, or cause area without extra complexity.' },
        { icon: <MessageSquare size={20} />, title: 'Community WhatsApp Reach', desc: 'Reach beneficiaries and volunteers via WhatsApp — the communication channel most communities already use.' },
        { icon: <FileText size={20} />, title: 'Simple Reporting', desc: 'Export ticket data for grant reporting, impact measurement, and donor accountability.' },
        { icon: <Shield size={20} />, title: 'Data Privacy', desc: 'Keep sensitive beneficiary information secure with role-based access and encrypted storage.' },
    ],
    steps: [
        { step: '01', title: 'Apply for NGO Pricing', desc: 'Contact our team with your registration details to receive discounted or sponsored access.' },
        { step: '02', title: 'Set Up Your Programs', desc: 'Create separate forms and workflows for each program, initiative, or community you serve.' },
        { step: '03', title: 'Focus on Your Mission', desc: 'Let KaizIQ Resolve handle the operational overhead while your team focuses on what matters most.' },
    ],
    accentColor: '#e11d48',
    ctaTitle: 'Empowering missions, not inboxes',
    ctaSubtitle: 'Ask us about our nonprofit program and special pricing.',
    ctaButton: 'Contact Us',
    ctaPath: null,
};

// ─── By Use Case ──────────────────────────────────────────────────────────────

export const itHelpDeskData = {
    tag: 'By Use Case',
    tagVariant: 'green',
    heroIcon: <Cpu size={40} />,
    title: 'IT Help Desk Built for Modern Teams',
    subtitle: 'Give your IT team a structured, accountable way to handle hardware requests, software issues, access provisioning, and everything in between.',
    stats: [
        { value: '45%', label: 'Faster resolution' },
        { value: '0', label: 'Emails lost' },
        { value: '100%', label: 'Ticket visibility' },
    ],
    features: [
        { icon: <FileText size={20} />, title: 'Structured Request Forms', desc: 'Custom intake forms for different request types — hardware, software, access, incidents — each with the right fields.' },
        { icon: <UserCog size={20} />, title: 'Smart Assignment', desc: 'Route tickets to the right IT sub-team automatically based on category, priority, or department.' },
        { icon: <Clock size={20} />, title: 'SLA Enforcement', desc: 'Set response and resolution targets by ticket type. Get alerts before SLAs breach.' },
        { icon: <Bell size={20} />, title: 'Status Notifications', desc: 'Keep requesters informed via WhatsApp and email — no more chasing the helpdesk for updates.' },
        { icon: <Shield size={20} />, title: 'Audit Trail', desc: 'Complete history of every action, comment, and escalation. Essential for compliance and post-mortems.' },
        { icon: <BarChart2 size={20} />, title: 'IT Metrics', desc: 'Track MTTR, ticket volume by category, SLA compliance, and agent workload distribution.' },
    ],
    steps: [
        { step: '01', title: 'Build Your Request Forms', desc: 'Create forms for each ticket category with custom fields that capture exactly what IT needs to act.' },
        { step: '02', title: 'Configure Routing & SLAs', desc: 'Set up assignment rules and SLA policies per priority level and ticket type.' },
        { step: '03', title: 'Track, Resolve & Report', desc: 'Manage the full lifecycle of every IT request with complete audit trails and performance analytics.' },
    ],
    accentColor: '#059669',
    ctaTitle: 'Bring order to your IT operations',
    ctaSubtitle: 'Start free and have your IT help desk live in under a day.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const hrOperationsData = {
    tag: 'By Use Case',
    tagVariant: 'green',
    heroIcon: <UserCog size={40} />,
    title: 'HR Operations Without the Email Chaos',
    subtitle: 'Centralise employee queries, onboarding requests, policy questions, and grievance submissions in a structured, trackable system your HR team will love.',
    stats: [
        { value: '60%', label: 'Less HR email' },
        { value: '3x', label: 'Faster query resolution' },
        { value: '100%', label: 'Request visibility' },
    ],
    features: [
        { icon: <FileText size={20} />, title: 'HR Request Forms', desc: 'Separate forms for leave requests, policy queries, payroll issues, and employee grievances — each with the right fields.' },
        { icon: <Shield size={20} />, title: 'Confidential Submissions', desc: 'Role-based access ensures sensitive HR tickets are only visible to authorised team members.' },
        { icon: <Bell size={20} />, title: 'Employee Notifications', desc: 'Employees receive WhatsApp updates on their request status — no need to follow up with HR.' },
        { icon: <Clock size={20} />, title: 'SLA by Request Type', desc: "Set different response targets for urgent queries vs routine requests. Ensure nothing falls through the cracks." },
        { icon: <BarChart2 size={20} />, title: 'HR Analytics', desc: 'Identify the most common employee concerns, track resolution trends, and measure HR team performance.' },
        { icon: <Users size={20} />, title: 'Department Segmentation', desc: 'Organise tickets by department, location, or employee level for easy filtering and reporting.' },
    ],
    steps: [
        { step: '01', title: 'Design Your HR Forms', desc: 'Create intake forms for each query type. Add conditional fields so employees give you exactly what you need.' },
        { step: '02', title: 'Route to the Right HR Partner', desc: 'Automatically assign queries to the responsible HR business partner or team based on category.' },
        { step: '03', title: 'Resolve with Full Accountability', desc: 'Track every HR interaction with a complete audit trail. Export records for compliance reporting.' },
    ],
    accentColor: '#059669',
    ctaTitle: 'Empower your HR team to do their best work',
    ctaSubtitle: 'Give employees a professional support experience while freeing HR from inbox overload.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const facilitiesData = {
    tag: 'By Use Case',
    tagVariant: 'green',
    heroIcon: <Wrench size={40} />,
    title: 'Facilities Management Made Accountable',
    subtitle: 'Track maintenance requests, repairs, and facility issues from submission to resolution. No more sticky notes, lost emails, or forgotten work orders.',
    stats: [
        { value: '80%', label: 'Fewer missed jobs' },
        { value: '2x', label: 'Faster acknowledgement' },
        { value: '100%', label: 'Work order visibility' },
    ],
    features: [
        { icon: <FileText size={20} />, title: 'Maintenance Request Forms', desc: 'Location-specific forms with fields for room number, issue category, urgency, and photo attachments.' },
        { icon: <Bell size={20} />, title: 'Instant WhatsApp Alerts', desc: 'Facilities staff receive ticket assignments and updates via WhatsApp — perfect for on-the-go teams.' },
        { icon: <Settings size={20} />, title: 'Vendor Assignment', desc: 'Assign tickets to internal maintenance staff or external contractors with custom workflows per category.' },
        { icon: <Clock size={20} />, title: 'Priority-Based SLAs', desc: 'Urgent issues like water leaks get same-day SLAs; routine requests follow standard resolution timelines.' },
        { icon: <BarChart2 size={20} />, title: 'Asset & Location Reporting', desc: 'Identify problem areas, high-maintenance assets, and recurring issues with built-in analytics.' },
        { icon: <CheckCircle size={20} />, title: 'Photo Documentation', desc: 'Attach before/after photos to tickets for visual verification and vendor accountability.' },
    ],
    steps: [
        { step: '01', title: 'Set Up Location Forms', desc: 'Create forms per building, floor, or area with the specific fields your facilities team needs.' },
        { step: '02', title: 'Route to the Right Team', desc: 'Auto-assign based on issue type — electrical to the electrician, plumbing to the plumber.' },
        { step: '03', title: 'Track to Completion', desc: 'Every job has a digital paper trail. Confirm completion with photos, comments, and requester sign-off.' },
    ],
    accentColor: '#059669',
    ctaTitle: 'Every facility request — tracked, assigned, resolved',
    ctaSubtitle: 'Give your facilities team the accountability tool they deserve.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const customerComplaintsData = {
    tag: 'By Use Case',
    tagVariant: 'green',
    heroIcon: <MessageSquare size={40} />,
    title: 'Turn Complaints Into Loyalty',
    subtitle: 'A customer who feels heard and resolved is more loyal than one who never complained. Build a complaint handling process that turns negative experiences into positive relationships.',
    stats: [
        { value: '92%', label: 'Resolution rate achievable' },
        { value: '24 hr', label: 'Average first response' },
        { value: '4.2★', label: 'Avg satisfaction after resolution' },
    ],
    features: [
        { icon: <Globe size={20} />, title: 'Multi-Channel Intake', desc: 'Capture complaints from your website, WhatsApp, QR codes, or kiosks — all flowing into one system.' },
        { icon: <Bell size={20} />, title: 'Instant Acknowledgement', desc: 'Auto-acknowledge every complaint within minutes via WhatsApp. Customers know they are being heard.' },
        { icon: <Clock size={20} />, title: 'Resolution SLAs', desc: 'Set customer-facing resolution timelines and escalate automatically when deadlines approach.' },
        { icon: <Shield size={20} />, title: 'Escalation Matrix', desc: 'Unresolved complaints automatically escalate to senior staff — preventing issues from being buried.' },
        { icon: <BarChart2 size={20} />, title: 'Complaint Analytics', desc: 'Identify root causes, repeat complainants, and high-risk categories before they become a pattern.' },
        { icon: <Star size={20} />, title: 'Satisfaction Tracking', desc: 'Close the loop with customers and measure resolution quality across your team.' },
    ],
    steps: [
        { step: '01', title: 'Publish Your Complaint Form', desc: 'Deploy a branded complaint form across all your customer touchpoints in minutes.' },
        { step: '02', title: 'Assign & Acknowledge Instantly', desc: 'Auto-assign to the right team and send an immediate acknowledgement to the customer via WhatsApp.' },
        { step: '03', title: 'Resolve, Track & Improve', desc: 'Monitor resolution quality with analytics. Identify systemic issues before they damage your reputation.' },
    ],
    accentColor: '#059669',
    ctaTitle: 'Every complaint is a chance to win a loyal customer',
    ctaSubtitle: 'Build a complaint management process that actually works.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const internalFeedbackData = {
    tag: 'By Use Case',
    tagVariant: 'green',
    heroIcon: <Lightbulb size={40} />,
    title: 'Capture Employee Feedback That Actually Gets Acted On',
    subtitle: 'Stop losing valuable internal feedback to email threads and annual surveys. Create a structured, continuous loop that shows your team their voice matters.',
    stats: [
        { value: '3x', label: 'More feedback submitted' },
        { value: '100%', label: 'Submissions tracked' },
        { value: '48 hr', label: 'Average response to feedback' },
    ],
    features: [
        { icon: <FileText size={20} />, title: 'Feedback Forms by Category', desc: 'Create separate forms for product feedback, culture suggestions, process improvements, and safety concerns.' },
        { icon: <Shield size={20} />, title: 'Anonymous Submission Option', desc: 'Give employees a safe channel for sensitive feedback with optional anonymity controls.' },
        { icon: <Bell size={20} />, title: 'Acknowledgement Loop', desc: 'Every submission gets acknowledged automatically. Employees see their feedback is received, not ignored.' },
        { icon: <Settings size={20} />, title: 'Route to Decision Makers', desc: 'Product feedback goes to product managers. Culture suggestions go to HR. Routing is fully configurable.' },
        { icon: <BarChart2 size={20} />, title: 'Feedback Trends', desc: 'Spot emerging concerns, popular ideas, and departmental patterns before they become problems.' },
        { icon: <CheckCircle size={20} />, title: 'Close the Loop', desc: 'Update the employee on what happened with their suggestion. Build a culture of listening.' },
    ],
    steps: [
        { step: '01', title: 'Design Feedback Channels', desc: 'Create topic-specific forms and decide whether each allows anonymous submissions.' },
        { step: '02', title: 'Distribute & Collect', desc: 'Share form links in Slack, email signatures, onboarding packs, and internal portals.' },
        { step: '03', title: 'Act & Communicate', desc: 'Route feedback to the right owners, take action, and communicate outcomes back to the team.' },
    ],
    accentColor: '#059669',
    ctaTitle: 'Make your team feel genuinely heard',
    ctaSubtitle: 'Build an internal feedback culture with structure and accountability.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

// ─── By Industry ──────────────────────────────────────────────────────────────

export const educationData = {
    tag: 'By Industry',
    tagVariant: 'purple',
    heroIcon: <GraduationCap size={40} />,
    title: 'Support That Serves Students & Staff',
    subtitle: 'Educational institutions deal with thousands of queries across admissions, IT, facilities, academics, and administration. KaizIQ Resolve brings order to the chaos.',
    stats: [
        { value: '10x', label: 'More structured than email' },
        { value: '1000s', label: 'Students served daily' },
        { value: '50%', label: 'Reduction in repeat queries' },
    ],
    features: [
        { icon: <Users size={20} />, title: 'Student & Staff Portals', desc: 'Separate intake forms for student queries, staff requests, and administrative workflows — all in one platform.' },
        { icon: <Globe size={20} />, title: 'Mobile-Friendly Forms', desc: 'Students submit requests from any device. Perfect for mobile-first campuses.' },
        { icon: <Settings size={20} />, title: 'Department Routing', desc: 'Admissions queries go to admissions, IT issues go to IT helpdesk, fee queries go to finance — automatically.' },
        { icon: <Bell size={20} />, title: 'WhatsApp Updates', desc: "Send ticket updates via WhatsApp — the channel students and parents actually check." },
        { icon: <BarChart2 size={20} />, title: 'Query Analytics', desc: 'Identify peak query periods, common pain points, and resolution bottlenecks by department.' },
        { icon: <Shield size={20} />, title: 'Data Privacy', desc: 'Student data handled in compliance with educational data protection standards.' },
    ],
    steps: [
        { step: '01', title: 'Set Up Department Forms', desc: 'Create dedicated forms for academics, IT, admissions, facilities, and student services.' },
        { step: '02', title: 'Route to the Right Team', desc: 'Queries automatically route to the correct department based on category and urgency.' },
        { step: '03', title: 'Resolve & Report', desc: 'Provide students and staff with timely resolutions. Export data for institutional reporting.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Build a campus where every query gets resolved',
    ctaSubtitle: 'Support students and staff better without increasing your administrative burden.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const healthcareData = {
    tag: 'By Industry',
    tagVariant: 'purple',
    heroIcon: <Stethoscope size={40} />,
    title: 'Healthcare Support That Keeps Care Teams Moving',
    subtitle: 'From patient queries to staff IT issues and facility maintenance, healthcare operations require structured, accountable, and secure ticket management.',
    stats: [
        { value: '100%', label: 'Ticket accountability' },
        { value: 'Encrypted', label: 'Data at rest & in transit' },
        { value: '24/7', label: 'Accessible on any device' },
    ],
    features: [
        { icon: <Lock size={20} />, title: 'Security First', desc: 'AES-256 encryption, role-based access control, and complete audit logs for every action taken.' },
        { icon: <Settings size={20} />, title: 'Clinical & Non-Clinical Workflows', desc: 'Separate workflows for patient-facing queries, internal IT, HR, and facilities — all in one platform.' },
        { icon: <Bell size={20} />, title: 'Instant Staff Notifications', desc: 'Care teams and support staff receive WhatsApp alerts for critical requests and escalations.' },
        { icon: <Shield size={20} />, title: 'Access Control', desc: 'Restrict sensitive patient-related tickets to authorised staff only with granular permission settings.' },
        { icon: <Clock size={20} />, title: 'Priority-Based SLAs', desc: 'Critical issues get immediate escalation. Routine requests follow standard service timelines.' },
        { icon: <BarChart2 size={20} />, title: 'Operational Reports', desc: 'Measure support performance across departments. Identify recurring issues before they affect patient care.' },
    ],
    steps: [
        { step: '01', title: 'Design Care Workflows', desc: 'Build structured intake forms for each support category with the right fields and privacy controls.' },
        { step: '02', title: 'Route with Precision', desc: 'Ensure every ticket reaches the right team with automated routing and escalation rules.' },
        { step: '03', title: 'Resolve & Audit', desc: 'Close tickets with complete documentation. Audit logs satisfy compliance requirements automatically.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Operational excellence for teams that save lives',
    ctaSubtitle: 'Keep your support operations as precise as your clinical operations.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const retailData = {
    tag: 'By Industry',
    tagVariant: 'purple',
    heroIcon: <ShoppingBag size={40} />,
    title: 'Fast Support for High-Volume Retail Operations',
    subtitle: 'Retail and e-commerce move at speed. Whether it is store staff, customer complaints, or supplier queries, KaizIQ Resolve keeps every request visible and resolved.',
    stats: [
        { value: '500+', label: 'Tickets handled / day' },
        { value: '15 min', label: 'Avg first response' },
        { value: '4.5★', label: 'Customer satisfaction' },
    ],
    features: [
        { icon: <Globe size={20} />, title: 'Omni-Channel Intake', desc: 'Accept tickets from your website, WhatsApp, QR codes in stores, and customer portals simultaneously.' },
        { icon: <Bell size={20} />, title: 'WhatsApp-Native Updates', desc: 'Customers receive order, complaint, and query updates directly on WhatsApp — no app download needed.' },
        { icon: <Settings size={20} />, title: 'Store & Channel Segmentation', desc: 'Separate queues for each store, region, or online channel. No cross-contamination of support queues.' },
        { icon: <Clock size={20} />, title: 'Peak Season Ready', desc: 'Handle ticket surges during sales events with add-on ticket packs and unlimited agent scalability.' },
        { icon: <Smartphone size={20} />, title: 'White-Label Customer App', desc: 'Give your customers a branded mobile support experience. Available on iOS and Android.' },
        { icon: <BarChart2 size={20} />, title: 'Customer Complaint Analytics', desc: 'Identify high-complaint products, stores, or processes. Fix issues before they scale.' },
    ],
    steps: [
        { step: '01', title: 'Deploy Across Touchpoints', desc: 'Set up intake forms for stores, online, and WhatsApp. QR codes make in-store submission instant.' },
        { step: '02', title: 'Route to the Right Team', desc: 'Store complaints go to store managers, logistics issues to fulfilment, returns to the ops team.' },
        { step: '03', title: 'Resolve at Retail Speed', desc: 'Close tickets fast with WhatsApp updates. Measure resolution performance across every channel.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Support that matches the pace of retail',
    ctaSubtitle: 'Handle high ticket volumes without sacrificing resolution quality.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};

export const financialServicesData = {
    tag: 'By Industry',
    tagVariant: 'purple',
    heroIcon: <Landmark size={40} />,
    title: 'Secure, Compliant Support for Financial Services',
    subtitle: 'Banks, NBFCs, and fintech companies need support infrastructure that is auditable, secure, and efficient. KaizIQ Resolve delivers on all three.',
    stats: [
        { value: 'SOC 2', label: 'Type II compliant' },
        { value: 'AES-256', label: 'Encryption standard' },
        { value: '100%', label: 'Ticket audit trail' },
    ],
    features: [
        { icon: <Lock size={20} />, title: 'End-to-End Encryption', desc: 'All ticket data encrypted at rest and in transit. Zero tolerance for data exposure.' },
        { icon: <Shield size={20} />, title: 'Regulatory Audit Logs', desc: 'Every action logged with timestamp and user ID. Exportable for RBI, SEBI, or internal compliance audits.' },
        { icon: <Settings size={20} />, title: 'Approval Workflows', desc: 'Multi-level approval chains for sensitive operations like account changes, limit increases, and exception requests.' },
        { icon: <Users size={20} />, title: 'Role-Based Access', desc: 'Strict access controls ensure agents only see the tickets and data relevant to their role.' },
        { icon: <Bell size={20} />, title: 'Customer Notifications', desc: 'Keep customers informed on query status via WhatsApp without exposing sensitive account details.' },
        { icon: <BarChart2 size={20} />, title: 'Compliance Reporting', desc: 'Generate reports on resolution times, escalation rates, and service quality for regulatory submissions.' },
    ],
    steps: [
        { step: '01', title: 'Configure Secure Workflows', desc: 'Set up intake forms, access controls, and approval chains aligned with your compliance requirements.' },
        { step: '02', title: 'Handle with Accountability', desc: 'Every ticket touched by every agent is logged. Escalation rules ensure no query goes unanswered.' },
        { step: '03', title: 'Report & Evidence', desc: 'Export complete audit trails and resolution reports for compliance, audits, and management reviews.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Compliance-ready support for regulated industries',
    ctaSubtitle: 'Built for teams where accountability is not optional.',
    ctaButton: 'Contact Sales',
    ctaPath: null,
};

export const manufacturingData = {
    tag: 'By Industry',
    tagVariant: 'purple',
    heroIcon: <Factory size={40} />,
    title: 'Keep Operations Running With Proactive Support',
    subtitle: 'On the factory floor, downtime costs money. KaizIQ Resolve gives plant managers and operations teams a fast, structured way to capture, route, and resolve maintenance and operations requests.',
    stats: [
        { value: '35%', label: 'Less unplanned downtime' },
        { value: '2x', label: 'Faster issue acknowledgement' },
        { value: '100%', label: 'Work order traceability' },
    ],
    features: [
        { icon: <Wrench size={20} />, title: 'Maintenance Work Orders', desc: 'Structured intake for breakdowns, preventive maintenance, safety issues, and equipment requests.' },
        { icon: <Bell size={20} />, title: 'Instant Mobile Alerts', desc: 'Field technicians receive WhatsApp notifications for new assignments — no desk required.' },
        { icon: <Settings size={20} />, title: 'Machine & Location Tagging', desc: 'Tag every ticket with machine ID, line number, or plant location for precise routing and reporting.' },
        { icon: <Clock size={20} />, title: 'Critical Issue SLAs', desc: 'Production stoppages get instant escalation. Routine PM follows scheduled SLA timelines.' },
        { icon: <CheckCircle size={20} />, title: 'Completion Sign-Off', desc: 'Technicians close tickets with photos and notes. Plant managers confirm completion with a single click.' },
        { icon: <BarChart2 size={20} />, title: 'MTTR & Reliability Reports', desc: 'Track Mean Time to Repair, recurring failure patterns, and technician performance across the plant.' },
    ],
    steps: [
        { step: '01', title: 'Map Your Equipment & Areas', desc: 'Build forms with machine IDs, line numbers, and plant areas as structured dropdown fields.' },
        { step: '02', title: 'Auto-Route to Technicians', desc: 'Issues route to the right maintenance team automatically based on machine type and shift schedule.' },
        { step: '03', title: 'Track to Root Cause', desc: 'Every ticket is a data point. Identify root causes, high-failure assets, and process improvements.' },
    ],
    accentColor: '#7c3aed',
    ctaTitle: 'Operations that run like a well-oiled machine',
    ctaSubtitle: 'Give your plant teams the accountability tools to prevent downtime.',
    ctaButton: 'Start Free Trial',
    ctaPath: '/login',
};
